/**
 * SavedViews — small dropdown that lists the user's saved view configs
 * (filters + sort + grouping + visible columns). Parent owns
 * persistence — typically by writing the list into
 * user_view_preferences.view_settings_jsonb.savedViews[].
 */
import { Bookmark, Plus, Trash2 } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../../lib/utils';
import type { SavedListView } from './list-types';

export interface SavedViewsProps {
  views: readonly SavedListView[];
  activeViewId: string | null;
  onSelect: (id: string | null) => void;
  onSaveCurrent: (name: string) => void;
  onDelete: (id: string) => void;
}

export function SavedViews({
  views,
  activeViewId,
  onSelect,
  onSaveCurrent,
  onDelete,
}: SavedViewsProps) {
  const [open, setOpen] = React.useState(false);
  const [naming, setNaming] = React.useState(false);
  const [draftName, setDraftName] = React.useState('');
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 rounded border border-border bg-background px-2 py-1 text-xs text-foreground hover:bg-foreground/[0.04]"
      >
        <Bookmark size={11} aria-hidden />
        Saved views {views.length > 0 && `(${views.length})`}
      </button>
      {open && (
        <div className="absolute right-0 top-full z-20 mt-1 w-56 rounded border border-border bg-background py-1 shadow-md">
          <button
            type="button"
            onClick={() => onSelect(null)}
            className={cn(
              'flex w-full items-center px-2 py-1 text-left text-xs hover:bg-foreground/[0.04]',
              activeViewId === null && 'font-semibold text-foreground',
            )}
          >
            Default view
          </button>
          {views.map((v) => (
            <div
              key={v.id}
              className={cn(
                'group flex items-center justify-between px-2 py-1 text-xs hover:bg-foreground/[0.04]',
                activeViewId === v.id && 'font-semibold text-foreground',
              )}
            >
              <button
                type="button"
                onClick={() => onSelect(v.id)}
                className="flex-1 truncate text-left"
              >
                {v.name}
              </button>
              <button
                type="button"
                onClick={() => onDelete(v.id)}
                className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:bg-foreground/[0.06] hover:text-foreground group-hover:opacity-100"
                aria-label={`Delete saved view ${v.name}`}
              >
                <Trash2 size={11} aria-hidden />
              </button>
            </div>
          ))}
          <div className="border-t border-border" />
          {naming ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (draftName.trim()) {
                  onSaveCurrent(draftName.trim());
                  setDraftName('');
                  setNaming(false);
                  setOpen(false);
                }
              }}
              className="flex items-center gap-1 px-2 py-1.5"
            >
              <input
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                placeholder="View name…"
                className="h-7 flex-1 rounded border border-border bg-background px-2 text-xs text-foreground focus:border-foreground/30 focus:outline-none"
              />
              <button
                type="submit"
                className="rounded border border-border bg-background px-2 py-1 text-2xs text-foreground hover:bg-foreground/[0.04]"
              >
                Save
              </button>
            </form>
          ) : (
            <button
              type="button"
              onClick={() => setNaming(true)}
              className="flex w-full items-center gap-1.5 px-2 py-1 text-left text-xs text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground"
            >
              <Plus size={11} aria-hidden /> Save current view
            </button>
          )}
        </div>
      )}
    </div>
  );
}
